import type { TenantSummary } from '@wms/domain';
import { useState } from 'react';
import { api } from '../../api';
import { store, useSession } from '../../session';
import { useLoad } from '../../ui';

const KEY = 'wms.catalog.tenant';

/** A link from another screen opens the catalog screens on this seller (a held order's product to map). */
export function pickSeller(tenantId: string): void {
  store.set(KEY, tenantId);
}

/**
 * Whose catalog the screen works on: sellers always their own (null); warehouse admins and
 * supervisors pick a seller, remembered across the catalog screens.
 */
export function useSellerPick() {
  const session = useSession();
  const staff = session.isOperator;
  const [tenantId, setTenantId] = useState<string | null>(() => (staff ? store.get(KEY) : null));
  const tenants = useLoad(() => (staff ? api<TenantSummary[]>('GET', '/api/admin/tenants') : Promise.resolve([] as TenantSummary[])), [staff]);
  const valid = tenants.data?.some((t) => t.id === tenantId) ? tenantId : null;
  return {
    staff,
    /** sellers: null (their own); staff: the picked seller or null */
    tenantId: staff ? valid : null,
    tenants: tenants.data ?? [],
    pick(id: string | null) {
      store.set(KEY, id);
      setTenantId(id);
    },
    /** "&tenantId=…" for staff, "" for sellers */
    query(prefix: '?' | '&' = '&') {
      return staff && valid ? `${prefix}tenantId=${valid}` : '';
    },
    /** whether a write can go ahead: sellers always, staff once a seller is picked */
    ready: !staff || Boolean(valid),
    canEdit: staff ? session.isSupervisor : session.me.role !== 'viewer',
  };
}

export function SellerSelect({ pick, allowAll = false }: { pick: ReturnType<typeof useSellerPick>; allowAll?: boolean }) {
  if (!pick.staff) return null;
  return (
    <select className="select" style={{ maxWidth: 280 }} value={pick.tenantId ?? ''} onChange={(e) => pick.pick(e.target.value || null)} aria-label="Satıcı">
      <option value="">{allowAll ? 'Tüm satıcılar' : 'Satıcı seçin'}</option>
      {pick.tenants
        .filter((t) => t.status !== 'offboarded')
        .map((t) => (
          <option key={t.id} value={t.id}>
            {t.code} · {t.displayName}
          </option>
        ))}
    </select>
  );
}
